import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import Layout from '../components/Layout';
import SEO from '../components/SEO';
import { 
  Check,
  X,
  ArrowRight,
  Award,
  Sparkles,
  Crown
} from 'lucide-react';

const Compare = () => {
  const navigate = useNavigate();

  const comparisonData = {
    features: [
      'Free Digital Profile',
      'NFC Business Cards',
      'Built-in CRM',
      'Business Card Scanner',
      'QR Code Generator',
      'Analytics Dashboard',
      'Team Management',
      'Custom Themes',
      'Indian Payment (INR)',
      'Local Customer Support',
      'Unlimited Links',
      'Contact Export'
    ],
    competitors: [
      {
        name: 'Piper Card',
        highlight: true,
        features: [true, true, true, true, true, true, true, true, true, true, true, true],
        price: 'Free - ₹99/mo'
      },
      {
        name: 'Linktree',
        features: [true, false, false, false, false, true, true, true, false, false, true, false],
        price: 'Free - $5/mo'
      },
      {
        name: 'Popl',
        features: [true, true, false, false, false, true, true, true, false, false, true, false],
        price: '$19.99+ cards'
      },
      {
        name: 'HiHello',
        features: [true, false, true, false, true, true, true, true, false, false, true, true],
        price: 'Free - $6/mo'
      },
      {
        name: 'Linq',
        features: [true, true, false, false, false, true, false, true, false, false, true, false],
        price: '$19.99+ cards'
      }
    ]
  };

  const whyPiperCard = [
    {
      title: 'Complete Ecosystem',
      description: 'Not just a link page or NFC card - a complete networking solution with CRM, analytics, and team features.'
    },
    {
      title: 'Indian Pricing',
      description: 'Affordable pricing in INR with local payment options like UPI, cards, and net banking.'
    },
    {
      title: 'Local Support',
      description: 'Customer support in your timezone, understanding local business needs.'
    },
    {
      title: 'More Value',
      description: 'Get NFC cards, CRM, scanner, and QR generator - all included in one platform.'
    }
  ];

  return (
    <Layout>
      <SEO 
        title="Compare Piper Card vs Linktree, Popl, HiHello | Best NFC Business Card"
        description="Compare Piper Card with Linktree, Popl, HiHello, and Linq. See why Piper Card is the best digital business card solution for professionals."
        keywords={['piper card vs linktree', 'popl alternative', 'hihello comparison', 'best NFC business card']}
      />

      <div className="min-h-screen">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white py-20 lg:py-28">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <div className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Award className="w-4 h-4" />
              Compare & Choose
            </div>
            <h1 className="text-4xl lg:text-6xl font-bold mb-6">
              How Piper Card <span className="text-amber-400">Compares</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto mb-10">
              See how Piper Card stacks up against other digital business card platforms. 
              We offer more features at better prices.
            </p>
          </div>
        </div>

        {/* Comparison Table */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr>
                    <th className="text-left p-4 bg-gray-50 rounded-tl-xl">Features</th>
                    {comparisonData.competitors.map((competitor, index) => (
                      <th 
                        key={index} 
                        className={`p-4 text-center ${
                          competitor.highlight 
                            ? 'bg-amber-500 text-white rounded-tr-xl' 
                            : 'bg-gray-50'
                        } ${index === comparisonData.competitors.length - 1 && !competitor.highlight ? 'rounded-tr-xl' : ''}`}
                      >
                        <div className="flex flex-col items-center gap-1">
                          {competitor.highlight && <Crown className="w-5 h-5" />}
                          <span className="font-bold">{competitor.name}</span>
                          <span className={`text-sm ${competitor.highlight ? 'text-amber-100' : 'text-gray-500'}`}>
                            {competitor.price}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.features.map((feature, featureIndex) => (
                    <tr key={featureIndex} className={featureIndex % 2 === 0 ? 'bg-gray-50/50' : ''}>
                      <td className="p-4 font-medium text-gray-900">{feature}</td>
                      {comparisonData.competitors.map((competitor, compIndex) => (
                        <td 
                          key={compIndex} 
                          className={`p-4 text-center ${competitor.highlight ? 'bg-amber-50' : ''}`}
                        >
                          {competitor.features[featureIndex] ? (
                            <Check className={`w-6 h-6 mx-auto ${competitor.highlight ? 'text-amber-600' : 'text-green-500'}`} />
                          ) : (
                            <X className="w-6 h-6 mx-auto text-gray-300" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Why Piper Card Section */}
        <div className="py-20 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Why Professionals Choose Piper Card
              </h2>
              <p className="text-xl text-gray-600">
                More than just features - it's about value
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whyPiperCard.map((item, index) => (
                <Card key={index} className="border-0 shadow-lg">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 bg-amber-500 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Check className="w-5 h-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-xl text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>

        {/* Detailed Comparisons */}
        <div className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
                Detailed Comparisons
              </h2>
            </div>

            <div className="grid lg:grid-cols-2 gap-8">
              {/* vs Linktree */}
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gray-900 text-white rounded-t-xl">
                  <CardTitle className="flex items-center justify-between">
                    <span>Piper Card vs Linktree</span>
                    <span className="text-amber-400 text-sm">Winner: Piper Card</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">
                    While Linktree is great for simple link pages, Piper Card offers a complete 
                    networking ecosystem with NFC cards, CRM, and business card scanning.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>NFC business cards included</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Built-in CRM for lead management</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>INR pricing for Indian users</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* vs Popl */}
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gray-900 text-white rounded-t-xl">
                  <CardTitle className="flex items-center justify-between">
                    <span>Piper Card vs Popl</span>
                    <span className="text-amber-400 text-sm">Winner: Piper Card</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">
                    Popl offers NFC products but lacks the comprehensive business tools that 
                    Piper Card provides. Plus, Popl's pricing is in USD, making it expensive for Indian users.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>60% cheaper for Indian users</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>CRM and scanner included</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Local customer support</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* vs HiHello */}
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gray-900 text-white rounded-t-xl">
                  <CardTitle className="flex items-center justify-between">
                    <span>Piper Card vs HiHello</span>
                    <span className="text-amber-400 text-sm">Winner: Piper Card</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">
                    HiHello is a solid digital business card app but doesn't offer physical 
                    NFC cards. Piper Card gives you both digital and physical solutions.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Physical NFC cards available</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Business card scanner included</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>QR code generator free</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* vs Traditional Cards */}
              <Card className="border-0 shadow-xl">
                <CardHeader className="bg-gray-900 text-white rounded-t-xl">
                  <CardTitle className="flex items-center justify-between">
                    <span>Piper Card vs Traditional Cards</span>
                    <span className="text-amber-400 text-sm">Smart Upgrade</span>
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <p className="text-gray-600 mb-4">
                    Traditional cards are great, but NFC cards take networking to the next level. 
                    Get all the benefits of a professional card plus digital superpowers.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Premium professional design</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Update anytime, no reprinting</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500" />
                      <span>Analytics on every share</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-500 py-20">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              Ready to Make the Switch?
            </h2>
            <p className="text-white/90 mb-8 text-lg">
              Join thousands who chose the better alternative
            </p>
            <Button 
              size="lg" 
              className="bg-white text-amber-600 hover:bg-gray-100 rounded-full px-10 py-6 text-lg shadow-xl"
              onClick={() => navigate('/shop')}
            >
              Get Started with Piper Card
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Compare;
